import router from '@/router'
import CheckoutService from '@/services/CheckoutService.js'
import PaymentMethodService from '@/services/PaymentMethodService.js'

export const namespaced = true

export const state = {
  paymentMethods: [],
  isFetchingPaymentMethods: false,
  activeReceipt: null,
  cashChangeAmount: null,
  isLoadingPayment: false,
  showReceiptDeliveryDialog: false,
  isProcessingDelivery: false,
  triggerRetryPayment: 0,
  lastSignedControlObject: null
}

export const mutations = {
  SET_PAYMENT_METHODS (state, val) {
    state.paymentMethods = val
  },
  SET_IS_FETCHING_PAYMENT_METHODS (state, val) {
    state.isFetchingPaymentMethods = val
  },
  SET_ACTIVE_RECEIPT (state, val) {
    state.activeReceipt = val
  },
  SET_CASH_CHANGE_AMOUNT (state, val) {
    state.cashChangeAmount = val
  },
  SET_IS_LOADING_PAYMENT (state, val) {
    state.isLoadingPayment = val
  },
  SET_SHOW_RECEIPT_DELIVERY_DIALOG (state, val) {
    state.showReceiptDeliveryDialog = val
    if (!val) {
      state.cashChangeAmount = null
    }
  },
  SET_IS_PROCESSING_DELIVERY (state, val) {
    state.isProcessingDelivery = val
  },
  TRIGGER_RETRY_PAYMENT (state) {
    state.triggerRetryPayment++
  },
  SET_LAST_SIGNED_CONTROL_OBJECT (state, val) {
    state.lastSignedControlObject = val
  }
}

export const actions = {
  fetchPaymentMethods ({ commit }) {
    commit('SET_IS_FETCHING_PAYMENT_METHODS', true)
    return PaymentMethodService.getPaymentMethods()
      .then(({ data }) => {
        commit('SET_PAYMENT_METHODS', data.data.payment_methods)
        commit('SET_IS_FETCHING_PAYMENT_METHODS', false)
      })
  },
  initiateSales ({ rootState, dispatch, commit }, { paymentMethod, amount, booking, parkedOrder, charges, accommodationChargesAmount = 0, customerCreditAmount = 0, shouldCheckOut = false }) {
    commit('SET_IS_LOADING_PAYMENT', true)
    const requiresSignature = rootState.posRegister.register.current_control_unit?.requires_client_signature
    const initiatePayment = () => {
      const paymentMethodId = paymentMethod ? paymentMethod.id : null
      const bookingId = booking ? booking.id : null
      const parkedOrderId = parkedOrder ? parkedOrder.id : null
      dispatch('posTerminal/resetStatusDialog', null, { root: true })
      CheckoutService.initiate({ registerId: rootState.posRegister.register.id, paymentMethodId, amount, accommodationChargesAmount, customerCreditAmount, charges, bookingId, parkedOrderId, shouldCheckOut })
        .then(({ data }) => {
          if (data.status === 'success') {
            console.log('initiateSales success', data.data.receipt)
            dispatch('handleReceiptResponse', data.data.receipt)
          } else {
            commit('SET_IS_LOADING_PAYMENT', false)
          }
        })
        .catch((error) => {
          console.log('prepareSales error', error)
          commit('SET_IS_LOADING_PAYMENT', false)
        })
    }

    if (requiresSignature) {
      // Kontrollerar om CleanCash fungerar innan vi börjar
      dispatch('posErrors/checkCleanCash', undefined, { root: true })
        .then(initiatePayment)
        .catch((error) => {
          console.error('checkCleanCash error', error)
          commit('SET_IS_LOADING_PAYMENT', false)
        })
    } else {
      initiatePayment()
    }
  },
  handleReceiptResponse ({ commit, dispatch, rootState }, receipt) {
    if (!receipt) {
      console.warn('handleReceiptResponse missing receipt')
      commit('SET_ACTIVE_RECEIPT', null)
      commit('SET_IS_LOADING_PAYMENT', false)
      commit('posTerminal/SET_TERMINAL_REMAINING_TIME', null, { root: true })
      return
    }

    commit('SET_ACTIVE_RECEIPT', receipt)

    if (receipt.receipt_status === 'awaiting_payment') {
      // Server tar hand om all terminalkommunikation och dialogdata
      const terminalTimeout = receipt.terminal_timeout
      console.log('awaiting payment', terminalTimeout)

      dispatch('posTerminal/startTerminalTimeout', {
        timeout: terminalTimeout,
        receiptId: receipt.id
      }, { root: true })
    } else {
      commit('posTerminal/SET_TERMINAL_REMAINING_TIME', null, { root: true })
    }

    if (receipt.receipt_status === 'awaiting_signature') {
      // Kommer bara hit om det är CleanCash
      dispatch('handleSignature')
    }

    if (receipt.receipt_status === 'awaiting_delivery') {
      dispatch('handleDelivery')
    }

    if (receipt.receipt_status === 'complete') {
      dispatch('handleCompletion')
    }

    if (receipt.receipt_status === 'error') {
      dispatch('handleError')
    }

    if (receipt.should_open_drawer && rootState.workstation.default_printer) {
      window.printer.openDrawer(rootState.workstation.default_printer.port)
    }
  },
  async retryPayment ({ dispatch, state }) {
    const receiptId = state.activeReceipt.id
    dispatch('posTerminal/resetStatusDialog', null, { root: true })
    CheckoutService.retryPayment({ receiptId })
      .then(({ data }) => {
        dispatch('handleReceiptResponse', data.data.receipt)
      })
  },
  async handleSignature ({ state, commit, dispatch }) {
    const currentControlObject = state.activeReceipt.control_object
    if (state.lastSignedControlObject && currentControlObject &&
        JSON.stringify(currentControlObject) === JSON.stringify(state.lastSignedControlObject)) {
      console.log('Duplikat, flera inkommande event så detta kan ignoreras')
      return
    }
    commit('SET_LAST_SIGNED_CONTROL_OBJECT', currentControlObject)

    // Skickar till CleanCash
    const response = await window.cleancash.registerReceipt(currentControlObject)
    CheckoutService.signReceipt({ receiptId: state.activeReceipt.id, controlData: response })
      .then(({ data }) => {
        dispatch('handleReceiptResponse', data.data.receipt)
      })
      .catch((error) => {
        // Kontrollenheten eller servern avvisade signeringen, kvittot kommer sättas till error
        console.log('signReceipt error', error)
        commit('SET_IS_LOADING_PAYMENT', false)
      })
  },

  handleDelivery ({ commit, dispatch }) {
    dispatch('snackbars/createSnackbar', {
      color: 'success',
      text: 'Registrering genomförd'
    }, { root: true })
    commit('SET_IS_PROCESSING_DELIVERY', false)
    commit('SET_SHOW_RECEIPT_DELIVERY_DIALOG', true)
  },

  async confirmDelivery ({ state, dispatch, commit }, { receiptForm, email }) {
    commit('SET_IS_PROCESSING_DELIVERY', true)
    if (receiptForm === 'print') {
      const result = await dispatch('print/print', state.activeReceipt.print, { root: true })
      console.log('confirmDelivery print result', result)
      if (result !== 'success') {
        commit('SET_IS_PROCESSING_DELIVERY', false)
        dispatch('snackbars/createSnackbar', {
          color: 'error',
          text: 'Kvittot kunde inte skrivas ut'
        }, { root: true })
        return
      }
    }
    CheckoutService.deliverReceipt({
      receiptId: state.activeReceipt.id,
      receiptForm,
      email
    })
      .then(({ data }) => {
        if (data.status === 'success') {
          commit('SET_SHOW_RECEIPT_DELIVERY_DIALOG', false)
          dispatch('handleReceiptResponse', data.data.receipt)
          dispatch('posReceipt/refreshDialogSilently', null, { root: true })
          if (data.data.booking) {
            const booking = data.data.booking
            window.enrich.enrichBooking(booking)
            commit('booking/ADD_BOOKING', booking, { root: true })
          }
        } else if (data.status === 'error' && data.error_code === 'invalid_email') {
          // Loader försvinner och man får ange en ny email eller välja något annat
          dispatch('snackbars/createSnackbar', {
            color: 'error',
            text: data.error_message
          }, { root: true })
        } else {
          commit('SET_SHOW_RECEIPT_DELIVERY_DIALOG', false)
        }
      })
      .finally(() => {
        commit('SET_IS_PROCESSING_DELIVERY', false)
      })
  },
  getReceipt ({ state, dispatch }, receiptId) {
    if (receiptId !== state.activeReceipt?.id) {
      return
    }
    CheckoutService.getReceipt({ receiptId })
      .then(({ data }) => {
        dispatch('handleReceiptResponse', data.data.receipt)
      })
  },
  handleCompletion ({ state, rootState, dispatch, commit }) {
    if (state.activeReceipt.receipt_type === 'copy' || (state.activeReceipt.receipt_type === 'return' && rootState.posReceipt.dialog.show)) {
      return
    }

    commit('SET_IS_LOADING_PAYMENT', false)

    const shouldCheckOut = state.activeReceipt.should_check_out
    const shouldResetCart = !shouldCheckOut
    const shouldCancelCart = shouldCheckOut

    if (shouldResetCart) {
      dispatch('posCart/reset', undefined, { root: true })
    } else if (shouldCancelCart) {
      dispatch('posCart/cancel', undefined, { root: true })
    }

    if (shouldCheckOut) {
      router.push({ name: 'ActionsItems' })
    }
  },
  handleError ({ commit, dispatch }) {
    commit('SET_ACTIVE_RECEIPT', null)
    commit('SET_IS_LOADING_PAYMENT', false)
    dispatch('snackbars/createSnackbar', {
      color: 'error',
      text: 'Kvittot kunde inte färdigställas'
    }, { root: true })
  },
  processRetryPayment ({ commit }) {
    commit('TRIGGER_RETRY_PAYMENT')
  }
}
